import {
  keepPreviousData,
  skipToken,
  useQuery,
  useQueryClient,
  type QueryFunctionContext,
} from '@tanstack/react-query';
import { useCurrentSimDataId } from '../SimViewer/useCurrentSimDataId';
import { dataApiClient } from '../../infrastructure/dataApi/client';
import { useApiDataFilter } from '../SimViewer/useApiDataFilter';
import { useAggregationConfig } from '../SimViewer/useAggregationConfig';
import type { Trip } from '../../types/domain';

type UseTripsData = {
  isLoading: boolean;
  trips: Trip[];
};

const EMPTY_DATA = [] as const satisfies Trip[];

const useTripsData = (): UseTripsData => {
  const queryClient = useQueryClient();
  const { currentSimDataId } = useCurrentSimDataId();
  const { apiFilter } = useApiDataFilter();
  const { aggregationConfig } = useAggregationConfig();

  const isRequired =
    [
      'trip_start',
      'trip_end',
      'trip_path',
      'traveled_distance',
      'traveled_time',
      'activity_time',
    ].includes(aggregationConfig?.target || '') &&
    ['grid_heatmap', 'heatmap', 'arcs'].includes(aggregationConfig?.type || '');
  const details = aggregationConfig?.type === 'arcs';

  const { data, isFetching } = useQuery({
    queryKey: ['trips', currentSimDataId, apiFilter, details],
    queryFn:
      currentSimDataId && isRequired
        ? async ({ signal }: QueryFunctionContext): Promise<Trip[]> => {
            return dataApiClient.getTrips(
              currentSimDataId,
              apiFilter,
              details,
              signal
            );
          }
        : skipToken,
    initialData: (): Trip[] | undefined => {
      // 詳細ありのデータは詳細なしのデータを包含しているため、キャッシュがあれば再取得せずに流用する
      return details
        ? undefined
        : queryClient.getQueryData([
            'trips',
            currentSimDataId,
            apiFilter,
            true,
          ]);
    },
    initialDataUpdatedAt: (): number | undefined => {
      return queryClient.getQueryState([
        'trips',
        currentSimDataId,
        apiFilter,
        true,
      ])?.dataUpdatedAt;
    },
    placeholderData: keepPreviousData,
    meta: { dataType: 'aggregation' },
  });

  return {
    isLoading: isFetching,
    trips: data && isRequired ? data : EMPTY_DATA,
  };
};

export { useTripsData };
